import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, concatMap, from, take } from 'rxjs';

import { AlertService } from '@app/shared/alert.service';
import { AttachService } from '@app/shared/attach/attach.service';
import { ImageCarouselComponent } from '@app/shared/attach/image-carousel/image-carousel.component';
import { QuotationService } from '@app/sales/quotation/quotation.service';
import { ObjectType } from '@app/shared/constants';
import { parseTz } from '@app/shared/time';

// La commande, puis un groupe par livraison ; `docs` est le tableau que le carrousel modifie en place (suppression, rang).
export interface DocSection {
  kind: 'order' | 'delivery';
  objecttyp: number; // ORDER (3) ou DELIVERY (4) : la cible d'un dépôt dans cette section
  objectid: number; // id de la commande ou de la livraison
  index: number; // rang du BL dans lnk_deliveries — c'est l'index attendu par la modale des BL
  title: string;
  note: string; // « signée le … » / « non livrée », vide sur la commande
  docs: any[];
  uploading: boolean;
}

@Component({
  selector: 'app-quotationdocs',
  templateUrl: './quotationdocs.component.html',
  styleUrls: ['./quotationdocs.component.scss'],
  imports: [ImageCarouselComponent, MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class QuotationdocsComponent implements OnChanges, OnInit, OnDestroy {
  @Input() orderId: number = 0;
  @Input() canEdit: boolean = true;
  @Input() closedLinesLoaded = false; // contrat de location : BL des seules lignes closes non lus tant que la F3 ne les a pas demandés
  @Input() exclude: ((a: any) => boolean) | null = null; // pièces que l'écran hôte montre ailleurs (ordonnances du dossier de location)
  @Input() withDeliveries = true; // faux : la commande seule (dossier de location, dont chaque matériel montre les pièces de ses BL)
  // Les pièces se relisent à chaque rechargement de la commande : impression, validation, acompte et
  // signature d'un BL AJOUTENT un PDF sans que l'id de la pièce change.
  @Input() loadOrderEventsSubject$?: Observable<any>;
  /** Clic sur le titre d'un BL : l'index du bon dans la commande, pour que l'écran ouvre sa modale. */
  @Output() openDelivery = new EventEmitter<number>();

  public sections: DocSection[] = [];
  public loading = false;
  public dragging: DocSection | null = null;

  private inFlightFor = 0; // id dont la lecture est en vol : le binding orderId et l'événement de rechargement arrivent ensemble
  private thumbs = new Map<number, string>(); // URL blob des vignettes par id de pièce, gardées d'un rechargement à l'autre (panneau monté par preserveContent)
  private destroyRef = inject(DestroyRef);

  constructor(
    private quotationService: QuotationService,
    private attachService: AttachService,
    private alertService: AlertService,
  ) {}

  ngOnInit(): void {
    this.loadOrderEventsSubject$?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.reload());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['orderId']) {
      this.sections = [];
      this.reload();
    }
  }

  ngOnDestroy(): void {
    this.thumbs.forEach(url => URL.revokeObjectURL(url));
    this.thumbs.clear();
  }

  get totalCount(): number {
    return this.sections.reduce((n, s) => n + s.docs.length, 0);
  }

  // Une requête pour la commande et ses BL, filtrée par objecttyp côté serveur ; /docsbyobjectid mêlerait les pièces d'un tiers de même id.
  reload(): void {
    const id = this.orderId;
    if (!id) {
      this.sections = [];
      return;
    }
    if (this.inFlightFor === id) return;
    this.inFlightFor = id;
    this.loading = true;
    const o = !this.withDeliveries ? '?o=attachs,open' : this.closedLinesLoaded ? '?o=deliveries,attachs' : '?o=deliveries,attachs,open';
    this.quotationService
      .getQuotationById(id, o)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (rows: any) => {
          this.inFlightFor = 0;
          this.loading = false;
          if (this.orderId !== id) return; // la commande a changé pendant l'aller-retour
          const order = (Array.isArray(rows) ? rows[0] : rows) ?? null;
          const deliveries: any[] = this.withDeliveries && Array.isArray(order?.lnk_deliveries) ? order.lnk_deliveries : [];
          this.sections = [
            { kind: 'order', objecttyp: ObjectType.ORDER, objectid: id, index: 0, title: 'Commande N°' + id, note: '', docs: this.usable(order?.lnk_attachs), uploading: false },
            ...deliveries.map((d: any, i: number) => ({
              kind: 'delivery' as const,
              objecttyp: ObjectType.DELIVERY,
              objectid: +d?.id,
              index: i, // l'ordre vient du serveur (ORDER BY del.id), le même que celui de la modale
              title: 'Livraison N°' + d?.id,
              note: d?.realdatdel ? 'signée le ' + (parseTz(d.realdatdel)?.format('DD/MM/YY HH:mm') ?? '') : 'non livrée', // heure MÉTIER (front.shared.time), pas celle du navigateur
              docs: this.usable(d?.lnk_attachs),
              uploading: false,
            })),
          ];
          this.releaseThumbs();
        },
        error: (e: any) => {
          this.inFlightFor = 0;
          this.loading = false;
          console.error('[QuotationdocsComponent] lecture des documents impossible', e);
          this.alertService.error('Documents de la commande illisibles');
        },
      });
  }

  // Le mémo d'un objet est rangé, lui aussi, dans c_attach (is_default_memo, text/html) : c'est du
  // texte de panneau, pas un document. Trié par RANG, comme le carrousel qui déplace d'un cran.
  private usable(list: any): any[] {
    const docs = (Array.isArray(list) ? list : [])
      .filter((a: any) => +a?.id > 0 && !this.hasFlag(a, 'is_default_memo') && !this.exclude?.(a))
      .sort((x: any, y: any) => (x.seq ?? Number.MAX_SAFE_INTEGER) - (y.seq ?? Number.MAX_SAFE_INTEGER) || +x.id - +y.id);
    return this.withThumbnails(docs);
  }

  // Pas generate_attachs_object_with_thumbnail : il ne garde que les media et ferait disparaître un .txt ou un .csv joint.
  // Une vignette déjà lue se reprend : chaque rechargement de la commande rend des objets neufs, sans thumbnailLocalUrl.
  private withThumbnails(docs: any[]): any[] {
    for (const attach of docs) {
      const known = this.thumbs.get(+attach.id);
      if (known) { attach.thumbnailLocalUrl = known; continue; }
      this.attachService
        .loadThumbnailImage$(attach)
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe((blob: any) => {
          const url = this.thumbs.get(+attach.id) ?? URL.createObjectURL(blob); // deux lectures croisées : une seule URL
          this.thumbs.set(+attach.id, url);
          attach.thumbnailLocalUrl = url;
        });
    }
    return docs;
  }

  // Les vignettes des pièces sorties de la liste (supprimées, autre commande) sont libérées à chaque lecture.
  private releaseThumbs(): void {
    const live = new Set(this.sections.flatMap(s => s.docs.map(d => +d.id)));
    this.thumbs.forEach((url, attachId) => {
      if (!live.has(attachId)) { URL.revokeObjectURL(url); this.thumbs.delete(attachId); }
    });
  }

  private hasFlag(doc: any, flag: string): boolean {
    const flags = doc?.data?.['flags'];
    return Array.isArray(flags) && flags.includes(flag);
  }

  /** Le titre d'un BL ouvre la modale des livraisons sur CE bon. */
  onTitleClick(section: DocSection): void {
    if (section.kind === 'delivery') this.openDelivery.emit(section.index);
  }

  onPickDocuments(section: DocSection, event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input?.files ?? []);
    input.value = ''; // sans ça, redéposer le MÊME fichier n'émet plus rien
    this.upload(section, files);
  }

  onDragOver(section: DocSection, event: DragEvent): void {
    if (!this.canEdit) return;
    event.preventDefault();
    this.dragging = section;
  }

  onDragLeave(): void {
    this.dragging = null;
  }

  onDrop(section: DocSection, event: DragEvent): void {
    if (!this.canEdit) return;
    event.preventDefault();
    this.dragging = null;
    this.upload(section, Array.from(event.dataTransfer?.files ?? []));
  }

  // En série : le serveur calcule le rang par MAX(seq)+1, deux envois simultanés le liraient identique. Cible = (objecttyp, objectid) de la section.
  private upload(section: DocSection, files: File[]): void {
    if (!files.length || !section.objectid || !this.canEdit) return;
    section.uploading = true;
    let refused = 0;
    from(files)
      .pipe(
        concatMap((file: File) => this.attachService.postAttach$(file, null, section.objecttyp, section.objectid)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        // Convention plateforme : un refus métier répond HTTP 200 + success:false.
        next: (r: any) => {
          if (r?.success === false) {
            refused++;
            this.alertService.error(r.error_msg ?? r.msg ?? 'Dépôt refusé');
          }
        },
        error: (e: any) => {
          section.uploading = false;
          console.error('[QuotationdocsComponent] dépôt impossible', e);
          this.alertService.error('ERREUR au dépôt du fichier');
          this.reload();
        },
        complete: () => {
          section.uploading = false;
          const ok = files.length - refused;
          if (ok > 0) this.alertService.success(ok > 1 ? ok + ' documents importés' : 'Document importé');
          this.reload();
        },
      });
  }
}
